import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Calculator, FileText, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, PeriodInput, Loading, Empty, StatPill, NativeSelect, Confirm } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { ceilMult, currentPeriod, fmtDateTime, num, periodLabel, supplyLabelOf } from "@/lib/format";
import { CustStatusBadge, KindBadge } from "@/components/badges";
import { SupplyPanel } from "@/components/SupplyPanel";

function TotalsTable({ est, mult }) {
  const rounded = (vid) => est.lines.filter((l) => l.voucher_type_id === vid).reduce((s, l) => s + ceilMult(l.estimate_qty, mult), 0);
  return (
    <div className="xl-wrap">
      <table className="xl" data-testid="estimate-totals-table">
        <thead><tr><th className="rownum">#</th><th>Jenis Voucher</th><th className="text-right">Terjual Aktual</th><th className="text-right">Estimasi / Pasokan</th><th className="text-right">Invoice (kelipatan {mult})</th></tr></thead>
        <tbody>
          {est.totals.map((t, i) => (
            <tr key={t.voucher_type_id}>
              <td className="rownum">{i + 1}</td><td className="font-medium">{t.voucher_name}</td>
              <td className="num">{num(t.sold_qty)}</td><td className="num font-semibold">{num(t.estimate_qty)}</td><td className="num text-emerald-700 font-semibold">{num(rounded(t.voucher_type_id))}</td>
            </tr>
          ))}
        </tbody>
        <tfoot><tr><td className="rownum">Σ</td><td>TOTAL</td><td className="num">{num(est.total_sold)}</td><td className="num" data-testid="estimate-total">{num(est.total_estimate)}</td><td className="num">{num(est.totals.reduce((s, t) => s + rounded(t.voucher_type_id), 0))}</td></tr></tfoot>
      </table>
    </div>
  );
}

function LinesTable({ est, mult }) {
  return (
    <div className="xl-wrap max-h-[50vh]">
      <table className="xl" data-testid="estimate-lines-table">
        <thead><tr><th className="rownum">#</th><th>Pelanggan</th><th>Status</th><th>Sumber</th><th>Jenis Voucher</th><th className="text-right">Terjual Aktual</th><th className="text-right">Estimasi / Pasokan</th><th className="text-right">Dibulatkan ke {mult}</th></tr></thead>
        <tbody>
          {est.lines.map((l, i) => (
            <tr key={`${l.customer_id}-${l.voucher_type_id}`} className={l.kind === "tidak_ada" ? "bg-slate-50 text-slate-500" : ""} data-testid={`estimate-line-${i}`}>
              <td className="rownum">{i + 1}</td><td>{l.customer_name}</td><td>{l.customer_status && <CustStatusBadge status={l.customer_status} />}</td><td><KindBadge kind={l.kind} testId={`estimate-kind-${i}`} /></td><td>{l.voucher_name}</td>
              <td className="num">{num(l.sold_qty)}</td><td className="num">{num(l.estimate_qty)}{(l.kind || "reguler") === "reguler" && <span className="text-[10px] text-amber-700 ml-1">(+{est.reserve_pct}%)</span>}</td><td className="num text-emerald-700">{num(ceilMult(l.estimate_qty, mult))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Estimate() {
  const { can, tenant } = useAuth();
  const nav = useNavigate();
  const mult = tenant?.round_multiple || 5;
  const [period, setPeriod] = useState(currentPeriod());
  const [pct, setPct] = useState(tenant?.reserve_pct ?? 30);
  const [list, setList] = useState(null);
  const [sel, setSel] = useState("");
  const reqId = useRef(0);
  const load = useCallback(async (selectId) => {
    const id = ++reqId.current;
    try {
      const all = (await api.get("/estimates")).data.filter((e) => e.period === period);
      if (id !== reqId.current) return;
      setList(all);
      setSel(selectId || all[0]?.id || "");
    } catch (e) { toast.error(errMsg(e)); }
  }, [period]);
  useEffect(() => {
    load();
  }, [load]);
  const est = list?.find((e) => e.id === sel);
  const create = async () => {
    try {
      const { data } = await api.post("/estimates", { period, reserve_pct: Number(pct) });
      toast.success(`Estimasi +${data.reserve_pct}% berhasil dihitung`);
      load(data.id);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const generate = async () => {
    try {
      const { data } = await api.post("/invoices/generate", { estimate_id: sel });
      toast.success(`${data.length} invoice draft dibuat`);
      nav(`/invoice?period=${period}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    try { await api.delete(`/estimates/${sel}`); toast.success("Estimasi dihapus"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader sheet="SHEET 04" title="Estimasi Pembuatan Voucher" subtitle={`Pelanggan AKTIF: penjualan aktual × ${100 + Number(pct)}% (dibulatkan ke atas). Pelanggan BARU/AKTIF KEMBALI: pasokan awal manual tanpa tambahan. Pelanggan BERHENTI: tidak ada pasokan. Periode ${periodLabel(period)} → ${supplyLabelOf(period)}.`}>
        <PeriodInput value={period} onChange={setPeriod} />
        {can("estimate:write") && (
          <>
            <div className="flex items-center gap-1"><span className="text-xs text-slate-600">Cadangan</span><Input type="number" min={0} value={pct} onChange={(e) => setPct(e.target.value)} className="w-20 h-9 font-mono" data-testid="estimate-pct-input" /><span className="text-xs">%</span></div>
            <Button onClick={create} className="bg-emerald-700 hover:bg-emerald-800" data-testid="estimate-calc-btn"><Calculator className="h-4 w-4 mr-2" />Hitung Estimasi</Button>
          </>
        )}
      </PageHeader>
      <SupplyPanel period={period} />
      {!list ? <Loading /> : !list.length ? <Empty>Belum ada estimasi untuk periode ini. Pastikan rekap sudah diisi, lalu klik "Hitung Estimasi".</Empty> : (
        <div className="space-y-4 fade-up">
          <div className="flex flex-wrap items-center gap-2">
            <NativeSelect value={sel} onChange={(e) => setSel(e.target.value)} data-testid="estimate-select">
              {list.map((e) => <option key={e.id} value={e.id}>{fmtDateTime(e.created_at)} · +{e.reserve_pct}%</option>)}
            </NativeSelect>
            <div className="flex-1" />
            {can("estimate:write") && <Confirm title="Hapus estimasi ini?" description="Invoice draft dari estimasi ini juga akan dihapus." onConfirm={del} testId="estimate-delete"
              trigger={<Button variant="outline" className="text-rose-600" data-testid="estimate-delete-btn"><Trash2 className="h-4 w-4 mr-2" />Hapus</Button>} />}
            {can("invoice:write") && <Button onClick={generate} className="bg-slate-900 hover:bg-slate-800" data-testid="estimate-generate-invoice-btn"><FileText className="h-4 w-4 mr-2" />Buat Invoice Akhir</Button>}
          </div>
          {est && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <StatPill label="Terjual Aktual" value={num(est.total_sold)} testId="estimate-stat-sold" />
                <StatPill label={`Estimasi Reguler (+${est.reserve_pct}%)`} value={num(est.summary?.regular_qty ?? est.total_estimate)} tone="green" testId="estimate-stat-regular" />
                <StatPill label="Pasokan Awal Baru" value={num(est.summary?.initial_new_qty || 0)} tone="blue" testId="estimate-stat-initial-new" />
                <StatPill label="Pasokan Awal Aktif Kembali" value={num(est.summary?.initial_reactivated_qty || 0)} tone="blue" testId="estimate-stat-initial-react" />
                <StatPill label="Berhenti (tanpa pasokan)" value={`${num(est.summary?.stopped_customers || 0)} pelanggan`} tone="amber" testId="estimate-stat-stopped" />
              </div>
              <p className="text-xs text-slate-500" data-testid="estimate-supply-label">{supplyLabelOf(est.period)} · Total estimasi/pasokan {num(est.total_estimate)} voucher · {num(est.lines.length)} baris</p>
              <TotalsTable est={est} mult={mult} />
              <h2 className="font-display font-semibold text-lg">Rincian per Pelanggan</h2>
              <LinesTable est={est} mult={mult} />
            </>
          )}
        </div>
      )}
    </div>
  );
}
